import type { ItemStatus, LineageItem, Stage } from '../types/lineage-images.api.types';
import type {
  ImagePairRead,
  ProductFeedRowRead,
  ProductImagesRead,
  ProductListingImagesRead,
  ProductListingRead,
  ProductMasterRead,
} from '../types/product-master.api.types';

import { toWireConnector } from './catalog-labels';
import type { MarketCommerceRow } from './curation-markets';
import { pairThumbUrl, pickFullUrl } from './image-urls';
import { formatScope } from './lineage-images-format';
import { sortStagesByFlow } from './lineage-images-search';
import { parseSourceProductId } from './source-product-id';

/** Primary images first, then by position — the order a listing shows its gallery in. */
function byRoleThenPosition(a: ImagePairRead, b: ImagePairRead): number {
  if (a.role !== b.role) {
    if (a.role === 'PRIMARY') return -1;
    if (b.role === 'PRIMARY') return 1;
  }
  return a.position - b.position;
}

/** The URL a viewer should see for one image: the optimized copy when it exists. */
function displayUrl(pair: ImagePairRead): string {
  return pair.optimized?.url ?? pair.sourceUrl;
}

/** "UK" for a listing's images block: its feed label, else the market its connector id names. */
function imageListingMarket(listing: ProductListingImagesRead): string {
  return (
    listing.feedLabel ??
    parseSourceProductId(listing.sourceProductId)?.country ??
    listing.language ??
    '—'
  );
}

/** One market the product sells in, with the feed rows (listings) curation linked there. */
export interface SoldInRow {
  key: string;
  listing: ProductListingRead;
  /** The feed row to open for this market (the listing's own, else the first linked one). */
  feedProductId: string | null;
  /** Every feed row linked to this market — more than one live row is a duplicate. */
  feedRows: ProductFeedRowRead[];
  /** The linked row the listing was derived from, for "matched by" and the run that matched it. */
  primaryRow: ProductFeedRowRead | null;
  thumbUrl: string | null;
  /** The same image full size (optimized, else source) — what the enlarged viewer opens. */
  fullUrl: string | null;
  duplicate: boolean;
  needsAttention: boolean;
  gone: boolean;
}

/** Sold-in rows plus the feed rows that belong to no market listing (gone or unmatched). */
export interface SoldInModel {
  rows: SoldInRow[];
  otherRows: ProductFeedRowRead[];
}

/** A Sold-in row's thumb + full image, or none. */
function rowImage(image: { thumb: string; full: string | null } | undefined): {
  thumbUrl: string | null;
  fullUrl: string | null;
} {
  return { thumbUrl: image?.thumb ?? null, fullUrl: image?.full ?? null };
}

/**
 * 3.8 "Sold in": one row per market listing with its price/availability, joined to the
 * feed rows curation linked to that market (same connector + feed label, or the listing's
 * own feed row) and to that row's first image — so the separate Markets and Matched
 * listings blocks collapse into one list. Rows that join no listing are returned apart.
 */
export function buildSoldIn(
  product: ProductMasterRead,
  images: ProductImagesRead | undefined,
): SoldInModel {
  const thumbs = new Map<string, { thumb: string; full: string | null }>();
  for (const listing of images?.listings ?? []) {
    const first = [...listing.images].sort(byRoleThenPosition)[0];
    // A 40px row thumb: the 160px thumbnail when recorded, else the optimized copy / source; the
    // viewer opens the full image.
    if (first) {
      thumbs.set(listing.feedProductId, {
        thumb: pairThumbUrl(first),
        full: pickFullUrl({ optimizedUrl: first.optimized?.url, sourceUrl: first.sourceUrl }),
      });
    }
  }
  const claimed = new Set<string>();
  const rows = product.listings.map((listing): SoldInRow => {
    const connector = toWireConnector(listing.connectorCode);
    const feedRows = product.feedRows.filter(
      (row) =>
        row.feedProductId === listing.feedProductId ||
        (toWireConnector(row.connectorCode) === connector && row.feedLabel === listing.marketCode),
    );
    feedRows.forEach((row) => claimed.add(row.feedProductId));
    const primaryRow =
      feedRows.find((row) => row.feedProductId === listing.feedProductId) ?? feedRows[0] ?? null;
    const feedProductId = listing.feedProductId ?? primaryRow?.feedProductId ?? null;
    return {
      key: listing.listingId,
      listing,
      feedProductId,
      feedRows,
      primaryRow,
      ...rowImage(
        (feedProductId ? thumbs.get(feedProductId) : undefined) ??
          feedRows.map((row) => thumbs.get(row.feedProductId)).find(Boolean),
      ),
      // The backend's own flag only: a duplicate needs the same connector, market, language AND
      // locale scope — re-deriving it from connector + feed label flagged distinct languages.
      duplicate: feedRows.some((row) => row.duplicate),
      needsAttention: feedRows.some((row) => row.needsAttention),
      gone: !!listing.absentSince,
    };
  });
  return { rows, otherRows: product.feedRows.filter((row) => !claimed.has(row.feedProductId)) };
}

/**
 * A sold-in row as the shared price / stock cells read it (curation's `MarketCommerceRow`):
 * the listing's market code, prices and availability, linked to the market's feed row.
 */
export function toCommerceRow(row: SoldInRow): MarketCommerceRow {
  return listingCommerceRow(row.listing, row.feedProductId);
}

/**
 * One market listing as a commerce row (market code, prices, availability) — used where no
 * sold-in join exists, e.g. a family member's or set part's own listings.
 */
export function listingCommerceRow(
  listing: ProductListingRead,
  feedProductId: string | null = listing.feedProductId,
): MarketCommerceRow {
  return {
    feedProductId,
    market: listing.marketCode,
    priceAmount: listing.priceAmount,
    priceCurrency: listing.priceCurrency,
    salePriceAmount: listing.salePriceAmount,
    salePriceCurrency: listing.salePriceCurrency,
    availability: listing.availability,
  };
}

/** One distinct image of the product and every market that shows it. */
export interface DedupedImage {
  key: string;
  /** The full image (optimized, else source) — the large view and the viewer. */
  url: string;
  /** The small rendition for strip thumbs (160px thumbnail when recorded, else `url`). */
  thumbUrl: string;
  pair: ImagePairRead;
  markets: string[];
  /** Feed rows showing it, so a tile can open the listing it belongs to. */
  feedProductIds: string[];
}

/**
 * 3.8 Images, de-duplicated: the UK and US copies of one photo are one tile ("shared by
 * UK and US"). Images are the same when their optimized content hash (else source URL)
 * matches; tiles keep primary-first, position order.
 */
export function dedupeProductImages(images: ProductImagesRead | undefined): DedupedImage[] {
  const byKey = new Map<string, DedupedImage>();
  for (const listing of images?.listings ?? []) {
    const market = imageListingMarket(listing);
    for (const pair of [...listing.images].sort(byRoleThenPosition)) {
      const key = pair.optimized?.contentHash ?? pair.sourceUrl;
      const existing = byKey.get(key);
      if (existing) {
        if (!existing.markets.includes(market)) existing.markets.push(market);
        existing.feedProductIds.push(listing.feedProductId);
      } else {
        byKey.set(key, {
          key,
          url: displayUrl(pair),
          thumbUrl: pairThumbUrl(pair),
          pair,
          markets: [market],
          feedProductIds: [listing.feedProductId],
        });
      }
    }
  }
  return [...byKey.values()].sort((a, b) => byRoleThenPosition(a.pair, b.pair));
}

/** How loudly a status should show when several markets / attempts disagree. */
const STATUS_WEIGHT: Record<ItemStatus, number> = {
  FAILED: 5,
  REVISIT: 4,
  PENDING: 3,
  SUCCESS: 2,
  SKIPPED: 1,
};

/** One run in a product's history, told as one line: when, where, and each stage's outcome. */
export interface RunHistoryEntry {
  runId: string;
  /** Newest item time in this run. */
  when: string;
  /** Each stage once, flow order, with its most notable newest-attempt outcome across markets. */
  stages: { stage: Stage; status: ItemStatus }[];
  /** Markets the run touched ("UK · en"), when the items were market-scoped. */
  markets: string[];
  /** What IMPORT decided for this product in this run (first market's disposition). */
  disposition: LineageItem['disposition'];
  /** Failed / held-back items, for a plain-words reason line. */
  issues: LineageItem[];
  /** Every item of this run (all stages, markets and attempts) — the step panel's detail. */
  items: LineageItem[];
}

/**
 * 3.8 History by run: collapses a page of lineage items (one per stage × market × attempt)
 * into one entry per run, newest run first unless `ascending`, so the product's history
 * reads as "what each run did" rather than a row per stage per listing.
 */
export function groupLineageByRun(items: LineageItem[], ascending = false): RunHistoryEntry[] {
  const byRun = new Map<string, LineageItem[]>();
  for (const item of items) {
    const list = byRun.get(item.runId);
    if (list) list.push(item);
    else byRun.set(item.runId, [item]);
  }
  const entries = [...byRun.entries()].map(([runId, runItems]): RunHistoryEntry => {
    // Newest attempt per (stage, market) first, so a retried stage shows where it ended.
    const newest = new Map<string, LineageItem>();
    for (const item of runItems) {
      const key = `${item.stage}|${item.scope}`;
      const current = newest.get(key);
      if (!current || item.attempt > current.attempt) newest.set(key, item);
    }
    const latest = [...newest.values()];
    const stageStatus = new Map<Stage, ItemStatus>();
    for (const item of latest) {
      const current = stageStatus.get(item.stage);
      if (!current || STATUS_WEIGHT[item.status] > STATUS_WEIGHT[current]) {
        stageStatus.set(item.stage, item.status);
      }
    }
    const markets = [
      ...new Set(latest.map((i) => formatScope(i.scope)).filter((m): m is string => !!m)),
    ];
    return {
      runId,
      when: runItems.reduce((max, i) => (i.createdAt > max ? i.createdAt : max), ''),
      stages: sortStagesByFlow(stageStatus.keys()).map((stage) => ({
        stage,
        status: stageStatus.get(stage) ?? 'PENDING',
      })),
      markets,
      disposition: latest.find((i) => i.stage === 'IMPORT' && i.disposition)?.disposition ?? null,
      issues: latest.filter((i) => i.status === 'FAILED' || i.status === 'REVISIT'),
      items: runItems,
    };
  });
  return entries.sort((a, b) =>
    ascending ? a.when.localeCompare(b.when) : b.when.localeCompare(a.when),
  );
}

/** The markets (raw scopes) a product's lineage touches, "*" (not market-scoped) excluded. */
export function lineageScopes(items: LineageItem[]): string[] {
  return [...new Set(items.map((i) => i.scope).filter((s) => !!formatScope(s)))].sort((x, y) =>
    (formatScope(x) ?? x).localeCompare(formatScope(y) ?? y),
  );
}

/** Where one market of a product stands now: its newest run and that run's last stage. */
export interface LineageNow {
  scope: string;
  runId: string;
  at: string;
  stage: Stage;
  status: ItemStatus;
  item: LineageItem;
}

/**
 * "Where it is now", per market: for each scope the newest run that touched it, and the last
 * stage (flow order) of that run's newest attempts — so the card leads with the current state,
 * not with history. Scope-less items ("*") count for every market they ran alongside.
 */
export function lineageNowByScope(items: LineageItem[]): LineageNow[] {
  const scopes = lineageScopes(items);
  const keys = scopes.length > 0 ? scopes : ['*'];
  return keys.flatMap((scope): LineageNow[] => {
    const mine = items.filter((i) => i.scope === scope || i.scope === '*' || !formatScope(i.scope));
    if (mine.length === 0) return [];
    const runAt = new Map<string, string>();
    for (const i of mine) {
      if ((runAt.get(i.runId) ?? '') < i.createdAt) runAt.set(i.runId, i.createdAt);
    }
    // Newest run wins; equal times fall back to the run id so the pick never depends on order.
    const [runId, at] =
      [...runAt.entries()].sort(
        (a, b) => b[1].localeCompare(a[1]) || b[0].localeCompare(a[0]),
      )[0] ?? [];
    if (!runId || !at) return [];
    const newest = new Map<Stage, LineageItem>();
    for (const i of mine) {
      if (i.runId !== runId) continue;
      const cur = newest.get(i.stage);
      if (!cur || i.attempt > cur.attempt) newest.set(i.stage, i);
    }
    const last = sortStagesByFlow(newest.keys()).at(-1);
    const item = last ? newest.get(last) : undefined;
    if (!last || !item) return [];
    return [{ scope, runId, at, stage: last, status: item.status, item }];
  });
}
